"use client"

import React from "react"

import { Button } from "@/shadcn/components/ui/button"
import {
  Card,
  CardContent,
} from "@/shadcn/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldError as ShadFieldError,
} from "@/shadcn/components/ui/field"
import { Input } from "@/shadcn/components/ui/input"
import { cn } from "@/lib/utils"
import ErrorModule from "app/mixins/ErrorModule.jsx"
import FieldError from "app/components/common/FieldError.jsx"

import { CARD, CARD_TITLE, SM_BUTTON } from "./styles"

export default function PasswordCard({
  editing,
  onStartEdit,
  onSave,
  onCancel,
  app,
  updatePasswordValue,
  isFieldInvalid,
}: {
  editing: boolean
  onStartEdit: () => void
  onSave: () => void
  onCancel: () => void
  app: any
  updatePasswordValue: () => void
  isFieldInvalid: (section: string) => Boolean
}) {
  const isPasswordInvalid = isFieldInvalid("password")
  const isPasswordConfirmationInvalid = isFieldInvalid("password-confirm")

  if (editing) {
    return (
      <Card className={CARD}>
        <CardContent className="tw:flex tw:flex-col tw:gap-0 tw:px-4 tw:pb-4">
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:py-4">
            <div className="tw:flex tw:w-full tw:flex-col tw:gap-1">
              <p className={CARD_TITLE}>Password</p>
            </div>
            <form
              className="tw:flex tw:w-full tw:flex-col tw:gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                onSave()
              }}
            >
              <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
                <Field data-invalid={isPasswordInvalid}>
                  <FieldLabel
                    htmlFor="new-password"
                    className="tw:text-sm tw:font-medium tw:leading-5 tw:text-foreground"
                  >
                    New Password
                  </FieldLabel>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="At least 8 characters"
                    className="tw:h-8 tw:rounded-lg tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5 tw:placeholder:text-muted-foreground"
                    onChange={updatePasswordValue}
                  />
                  <FieldDescription className="tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
                    Must be at least 8 characters with one number and one symbol.
                  </FieldDescription>
                  <ErrorModule
                    app={app}
                    keys={['USER_UPDATE']}
                    render={(state: any) => {
                      return <FieldError field="password" errors={state.errors} element={ShadFieldError} />
                    }}
                  />
                </Field>
              </div>
              <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
                <Field data-invalid={isPasswordConfirmationInvalid}>
                  <FieldLabel
                    htmlFor="confirm-password"
                    className="tw:text-sm tw:font-medium tw:leading-5 tw:text-foreground"
                  >
                    Confirm new password
                  </FieldLabel>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Re-enter new password"
                    className="tw:h-8 tw:rounded-lg tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5 tw:placeholder:text-muted-foreground"
                    onChange={updatePasswordValue}
                  />
                  <ErrorModule
                    app={app}
                    keys={['USER_UPDATE']}
                    render={(state: any) => {
                      return <FieldError field="password-confirm" errors={state.errors} element={ShadFieldError} />
                    }}
                  />
                </Field>
              </div>
              <div className="tw:flex tw:justify-end tw:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(SM_BUTTON, "tw:w-fit")}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="sm"
                  className={cn(SM_BUTTON, "tw:w-fit")}
                >
                  Save changes
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={CARD}>
      <div className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-4 tw:py-4">
        <p className={cn(CARD_TITLE, "tw:flex-1")}>Password</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(SM_BUTTON, "tw:shrink-0")}
          onClick={onStartEdit}
        >
          Change password
        </Button>
      </div>
    </Card>
  )
}
